import React, { ReactElement, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom';

import moment from 'moment';
import Title from './Title';
import Description from './Description';
import Data from './Data';
import Inquiry from './Inquiry';
import Connections from './Connections/Connections';
import ProjectMap from './ProjectMap';
import Properties from './Properties';

import s from './Portfolio.module.scss';
import { Project } from '../../shared/schema/ProjectSchema';
import Loading from '../Loading';
import { Property } from '../../shared/schema/PropertySchema';
import NotFound from '../notFound';
import { uploadedImageDir } from '../../utils/configs';
import { isAddressCoordinates } from '../../shared/stringUtils';
import CustomerCard from './CustomerCard/CustomerCard';
import usePageTracking from '../../hooks/usePageTracking';

const PROJECT_QUERY = gql`
  query project($url: String!) {
    getPublicProject(url: $url) {
      id
      name
      updatedAt
      status
      address
      zip
      city
      state
      buyOptions
      projectStatus
      projectType
      financing
      rooms
      area
      flatCount
      buildingDone
      description
      connections {
        id
        transportTypes
        publicTransport
        publicTransportStation
        travelTime
        connection
      }
      images {
        visible
        path
      }
      customer {
        id
        businessName
        description
        logoPath
        url
      }
      properties {
        id
        name
        rooms
        area
        price
        floor
        flatNumber
        stairs
        type
        images {
          path
        }
      }
      contacts {
        name
        phone1
      }
    }
  }
`;

interface MatchParams {
  url: string;
}

type PortfolioProps = RouteComponentProps<MatchParams>;

export default function Portfolio(props: PortfolioProps): ReactElement {
  const { match } = props;
  const { url } = match.params;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { loading, data } = useQuery(PROJECT_QUERY, {
    variables: { url },
  });
  const [propertyInquiry, setPropertyInquiry] = useState(null);

  usePageTracking(`Neubauprojekt in Wien`);

  if (loading || !data) return <Loading />;
  if (!data.getPublicProject) return <NotFound />;

  const {
    id,
    zip,
    city,
    state,
    address,
    name,
    description,
    updatedAt,
    properties,
    connections,
    customer,
    contacts,
    images,
  }: Project = data.getPublicProject;

  const onPropertyClicked = (property: Property) => {
    let stairName = null;
    if (property.stairs) {
      // eslint-disable-next-line no-restricted-globals
      stairName = isNaN(property.stairs as any)
        ? property.stairs
        : `Stiege ${parseFloat(property.stairs)}`;
    }
    setPropertyInquiry(
      `Bitte um Zusendung weiterer Unterlagen zu:\n\n${name}\n${
        stairName ? `${stairName}` : ''
      }${property.flatNumber ? ` / Top ${property.flatNumber}` : ''}`
    );
  };

  const metaModifiedTimeString = moment(parseInt(updatedAt, 10)).format();

  const headerImagePath =
    images?.length > 0 ? `${uploadedImageDir}/${images[0].path}` : null;
  const zipCity = `${zip} ${city}`;

  return (
    <>
      <Helmet>
        <title>
          {isAddressCoordinates(address) ? name : address} - Neubauprojekt in
          Wien {zip} von {customer.businessName}
        </title>
        <meta name="description" content={description} />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={`${address} - Neubauprojekt in Wien ${zip} von ${customer.businessName}`}
        />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://www.neubauprojekte.wien/portfolio/${url}/`}
        />
        <meta
          property="article:modified_time"
          content={metaModifiedTimeString}
        />
        {headerImagePath && (
          <meta
            property="og:image"
            content="https://www.neubauprojekte.wien/wp-content/uploads/neubauprojekt-1020-wien-dammhaufengasse%204/neubauprojekt-1020-wien-dammhaufengasse%204-1.jpg"
          />
        )}
        {headerImagePath && <meta property="og:image:height" content="500" />}
      </Helmet>

      <Title id={id} name={name} zipCity={zipCity} updatedAt={updatedAt} />
      <div className={s.content}>
        <div className={s.leftCol}>
          <Description project={data.getPublicProject} />
          <Data project={data.getPublicProject} />
          <Connections connections={connections} />
        </div>
        <div className={s.rightCol}>
          {properties.length > 0 && (
            <Properties
              data={properties}
              handlePropertyClicked={(property) => {
                onPropertyClicked(property);
                scrollRef.current.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          )}
          <div ref={scrollRef}>
            <Inquiry
              customer={customer}
              project={data.getPublicProject}
              defaultText={propertyInquiry}
            />
          </div>
          <ProjectMap address={address} zip={zip} state={state} />
          <CustomerCard customer={customer} contacts={contacts} />
        </div>
      </div>
    </>
  );
}
